import { useRouteMatch } from 'quartz-routing/react-router';
import { useAppContextQuery } from '../../hooks/useAppContextQuery';
import { Paths } from '../../constants/paths';
import { GET_PLACEMENTS_QUERY } from '../../../__generated__/chirp/com/hubspot/marketplace/discovery/studio/rpc/PlacementsRpc.dfc.new';
import { GET_PLACEMENTS_QUERY as PUBLIC_GET_PLACEMENTS_QUERY } from '../../../__generated__/chirp/com/hubspot/marketplace/discovery/studio/rpc/PlacementsRpc-external.dfc.new';
import { CalloutPlacementDiscriminant, CollectionPlacementDiscriminant } from '../../../__generated__/chirp/com/hubspot/marketplace/discovery/studio/rpc/PlacementsRpc';
import { PAGE_CATALOG } from 'marketplace-discovery-studio-lib/placements/catalog';

/**
 * Legacy hero CTA behaviors that aren't part of the studio callout model yet.
 * Read these off a placement's `calloutContent` via {@link getCalloutCta}.
 */

/** Reads the legacy CTA fields off a callout placement's content. */
export function getCalloutCta(placement) {
  return placement.calloutContent;
}
const PATH_TO_PAGE_ID = {
  [Paths.HOME]: 'HOME',
  [Paths.APPS]: 'APPS',
  [Paths.AGENTS]: 'AGENTS',
  [Paths.FEATURED_COLLECTION]: 'FEATURED_COLLECTION',
  [Paths.SOLUTIONS_PARTNERS]: 'SOLUTIONS_PARTNERS',
  [Paths.WORKFLOW_TEMPLATES]: 'WORKFLOWS'
};

/** Maps the active route to a placements `PageId` (or undefined if unmapped). */
export function useCurrentPageId() {
  const currentMatch = useRouteMatch();
  return currentMatch !== null && currentMatch !== void 0 && currentMatch.path ? PATH_TO_PAGE_ID[currentMatch.path] : undefined;
}

/**
 * Ordered banner callout placements (`BANNER_1`, `BANNER_2`, ...) for the
 * current page. Used to render banners interleaved between page sections
 * rather than stacked under the top callout row.
 */
export const BANNER_SECTIONS = ['BANNER_1', 'BANNER_2'];
export function isCalloutPlacement(p) {
  return p.entityType === CalloutPlacementDiscriminant || 'calloutContent' in p;
}
export function isCollectionPlacement(p) {
  return p.entityType === CollectionPlacementDiscriminant || 'collectionPreview' in p;
}

/**
 * Ordered collection section ids for a placements page, derived from the
 * Discovery Studio page catalog so new collection slots are picked up
 * automatically.
 */
export function getCollectionSectionsForPage(pageId) {
  if (!pageId) {
    return [];
  }
  const page = PAGE_CATALOG[pageId];
  if (!page) {
    return [];
  }
  return page.sections.filter(section => section.allowedEntityTypes.includes('COLLECTION')).map(section => section.id);
}

/** Ordered collection placements for the given section ids (skips empty slots). */
export function getCollectionPlacementsForSections(placementsBySection, sections) {
  return sections.map(section => placementsBySection[section]).filter(placement => Boolean(placement) && isCollectionPlacement(placement));
}

/** Inlined collection previews for the given section ids (skips empty slots). */
export function getCollectionPreviewsForSections(placementsBySection, sections) {
  return getCollectionPlacementsForSections(placementsBySection, sections).map(placement => placement.collectionPreview);
}

/** Collection previews for the catalog collection slots on a page. */
export function getPageCollectionPreviews(placementsBySection, pageId) {
  return getCollectionPreviewsForSections(placementsBySection, getCollectionSectionsForPage(pageId));
}
export function usePageCalloutBanners() {
  const pageId = useCurrentPageId();
  const {
    placementsBySection
  } = usePagePlacements(pageId);
  return BANNER_SECTIONS.map(section => placementsBySection[section]).filter(placement => Boolean(placement) && isCalloutPlacement(placement));
}
function toPlacementsBySection(placements) {
  const placementsBySection = {};
  for (const placement of placements) {
    placementsBySection[placement.section] = placement;
  }
  return placementsBySection;
}

/** Returns published placements for the given storefront page. */
export function usePagePlacements(pageId) {
  var _data$result$placemen, _data$result;
  const {
    data,
    loading,
    error
  } = useAppContextQuery({
    app: GET_PLACEMENTS_QUERY,
    public: PUBLIC_GET_PLACEMENTS_QUERY,
    options: {
      skip: !pageId,
      variables: {
        page: pageId,
        published: true
      }
    }
  });
  const placements = (_data$result$placemen = data === null || data === void 0 || (_data$result = data.result) === null || _data$result === void 0 ? void 0 : _data$result.placements) !== null && _data$result$placemen !== void 0 ? _data$result$placemen : [];
  return {
    placementsBySection: toPlacementsBySection(placements),
    loading,
    error
  };
}